import { cn } from '@resolute/ui';
import { ChevronDown } from 'lucide-react';
import { useId, useState } from 'react';
import { useController, type Control, type FieldValues, type Path } from 'react-hook-form';

import {
  countryByIso,
  DEFAULT_ISO,
  FREQUENT_COUNTRIES,
  OTHER_COUNTRIES,
} from './phone-countries';

/**
 * Phone number field with an explicit country-code dropdown.
 *
 * The form value stays a SINGLE string — `"+971 501234567"` — so the shared
 * `PhoneSchema` and the CRM payload keep the shape they already had; only how
 * the user builds that string changes.
 *
 * The dial code lives in local state rather than being parsed back out of the
 * value: `+1` belongs to both the US and Canada, so a stored string cannot tell
 * you which was picked. Keeping it here also means `reset()` clears the number
 * without resetting the country the visitor chose.
 *
 * The classes below mirror `@resolute/ui`'s `Input`/`Select` rather than
 * reusing them: this is one labelled control made of two elements, and the
 * dropdown needs `<optgroup>`, which `Select` does not render.
 */

const CONTROL_BASE =
  'h-11 border border-input bg-background text-sm text-foreground shadow-sm ' +
  'transition-[color,box-shadow,border-color] duration-fast ease-out-expo ' +
  'focus-visible:z-10 focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 ' +
  'focus-visible:ring-ring/60 focus-visible:ring-offset-1 focus-visible:ring-offset-background ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

/**
 * Builds the single stored string from the chosen code and what was typed.
 *
 * Only leading space is trimmed, never trailing: the field re-renders from the
 * stored value on every keystroke, so trimming the end would swallow the space
 * the moment it is typed and make "+675 7654321" impossible to enter. The
 * schema trims before validating, so a trailing space costs nothing.
 *
 * A number typed with its own `+` is taken verbatim and the dropdown ignored —
 * so a country missing from the curated list is never a dead end. See the note
 * in `phone-countries.ts`.
 */
function combine(dial: string, national: string): string {
  const value = national.trimStart();
  // Empty stays empty rather than becoming a bare "+91 ", so a skipped field
  // reads as missing to the required check instead of as a malformed number.
  if (!value) return '';
  return value.startsWith('+') ? value : `${dial} ${value}`;
}

/** Strips the selected dial code back off so the input shows just the national
 *  part — and leaves a verbatim `+` number alone. */
function nationalPart(value: string, dial: string): string {
  if (!value) return '';
  return value.startsWith(`${dial} `) ? value.slice(dial.length + 1) : value;
}

export interface PhoneFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  error?: string;
  required?: boolean;
  /** Placeholder for the national-number part only. */
  placeholder?: string;
}

export function PhoneField<T extends FieldValues>({
  control,
  name,
  label,
  error,
  required,
  placeholder = '98765 43210',
}: PhoneFieldProps<T>) {
  const autoId = useId();
  const inputId = `${autoId}-number`;
  const [iso, setIso] = useState(DEFAULT_ISO);
  const { field } = useController({ control, name });

  const dial = countryByIso(iso).dial;
  const value = (field.value as string) ?? '';
  const national = nationalPart(value, dial);
  const describedBy = error ? `${inputId}-error` : `${inputId}-hint`;

  function onCountryChange(nextIso: string) {
    setIso(nextIso);
    // Re-emit so the stored value carries the newly chosen code immediately,
    // rather than only after the next keystroke.
    field.onChange(combine(countryByIso(nextIso).dial, national));
  }

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="text-sm font-medium text-foreground">
        {label}
        {required && (
          <span className="ml-0.5 text-danger" aria-hidden="true">
            *
          </span>
        )}
      </label>

      <div className="flex">
        <div className="relative shrink-0">
          <select
            aria-label="Country code"
            value={iso}
            onChange={(e) => onCountryChange(e.target.value)}
            onBlur={field.onBlur}
            className={cn(
              CONTROL_BASE,
              'w-[7.25rem] appearance-none rounded-l rounded-r-none border-r-0 pl-3 pr-8',
              error && 'border-danger',
            )}
          >
            <optgroup label="Frequently used">
              {FREQUENT_COUNTRIES.map((c) => (
                <option key={c.iso} value={c.iso}>
                  {c.iso} {c.dial}
                </option>
              ))}
            </optgroup>
            <optgroup label="All countries">
              {OTHER_COUNTRIES.map((c) => (
                <option key={c.iso} value={c.iso}>
                  {c.iso} {c.dial}
                </option>
              ))}
            </optgroup>
          </select>
          <ChevronDown
            className="pointer-events-none absolute right-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
        </div>

        <input
          id={inputId}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          placeholder={placeholder}
          ref={field.ref}
          name={field.name}
          value={national}
          onChange={(e) => field.onChange(combine(dial, e.target.value))}
          onBlur={field.onBlur}
          className={cn(
            CONTROL_BASE,
            'w-full min-w-0 rounded-l-none rounded-r px-3.5 placeholder:text-muted-foreground',
            'hover:border-foreground/20',
            error && 'border-danger ring-danger/40',
          )}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          aria-required={required || undefined}
        />
      </div>

      {error ? (
        <p id={`${inputId}-error`} className="text-xs text-danger" role="alert">
          {error}
        </p>
      ) : (
        <p id={`${inputId}-hint`} className="text-xs text-muted-foreground">
          Country not listed? Type the full number with its code.
        </p>
      )}
    </div>
  );
}
